import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Rating,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    StarHalf as StarHalfIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    ContentCopy as ContentCopyIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import MiniDrawer from '../../../Components/Drawer';
import BaseSetting from '../../../Apis/setting';
import { getApiData } from '../../../Apis/apiHelper';
import styles from './styles';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
};

const getStarColor = (rating) => {
    if (rating >= 4) return '#4CAF50'; // Green for high ratings
    if (rating >= 3) return '#FF9800'; // Orange for medium ratings
    return '#F44336'; // Red for low ratings
};

const CompanyUserRating = () => {
    const [ratings, setRatings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedRating, setSelectedRating] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [ratingToDelete, setRatingToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [starFilter, setStarFilter] = useState('all');
    const [copiedStates, setCopiedStates] = useState({});
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getAllRatings = async () => {
        setLoading(true);
        try {
            const url = BaseSetting.endpoint.getAllRatingList;
            const response = await getApiData(BaseSetting?.apiUrl + url, 'POST', {
                page: 1,
                limit: 10,
            }, {}, true);
            if (response && response.status && Array.isArray(response.data)) {
                // Map API response to component's expected format
                const mappedRatings = response.data.map((item, index) => ({
                    id: item.id || index + 1,
                    rating: item.rating || 0,
                    review: item.review || '',
                    sender: {
                        name: `${item.sender_user?.first_name || ''} ${item.sender_user?.last_name || ''}`.trim() || 'Unknown',
                        email: item.sender_user?.email || '',
                        phone: '', // API doesn't provide phone
                        company: '' // API doesn't provide company
                    },
                    recipient: {
                        name: `${item.recipient_user?.first_name || ''} ${item.recipient_user?.last_name || ''}`.trim() || 'Unknown',
                        email: item.recipient_user?.email || '',
                        phone: '', // API doesn't provide phone
                        company: '' // API doesn't provide company
                    },
                    date: formatDate(item.createdAt || item.updatedAt),
                    status: 'active',
                }));
                setRatings(mappedRatings);
            } else {
                // Fallback to empty array if no valid data
                setRatings([]);
            }
        } catch (error) {
            console.error('Error fetching ratings:', error);
            toast('Error fetching ratings', { type: 'error' });
            // Use mock data as fallback when API fails
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        setDeleteLoading(true);
        try {
            const url = BaseSetting.endpoint.deleteRating;
            const response = await getApiData(BaseSetting?.apiUrl + url, 'POST', { id }, {}, true);

            if (response && response.status) {
                // Remove the rating from local state
                setRatings(ratings.filter(rating => rating.id !== id));
                toast('Rating deleted successfully', { type: 'success' });
            } else {
                toast(response?.message || 'Failed to delete rating', { type: 'error' });
            }
        } catch (error) {
            console.error('Error deleting rating:', error);
            toast('Error deleting rating', { type: 'error' });
        }
        // Close confirmation dialog and reset loading state
        setDeleteLoading(false);
        setDeleteConfirmDialog(false);
        setRatingToDelete(null);
    };

    const confirmDelete = (rating) => {
        setRatingToDelete(rating);
        setDeleteConfirmDialog(true);
    };

    useEffect(() => {
        getAllRatings();
    }, []);

    const handleRefresh = () => {
        getAllRatings();
    };

    const handleOpenDialog = (rating) => {
        setSelectedRating(rating);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRating(null);
    };

    const handleCopyClick = (id, review) => {
        navigator.clipboard.writeText(review);
        setCopiedStates((prevState) => ({
            ...prevState,
            [id]: true,
        }));
        setTimeout(() => {
            setCopiedStates((prevState) => ({
                ...prevState,
                [id]: false,
            }));
        }, 3000);
        toast('Review copied to clipboard!', { type: 'success' });
    };

    const filteredRatings = ratings.filter(rating => {
        if (starFilter === 'all') return true;
        return rating.rating === parseInt(starFilter);
    });

    const averageRating = ratings.length > 0
        ? (ratings.reduce((sum, rating) => sum + rating.rating, 0) / ratings.length).toFixed(1)
        : 0;

    const columns = [
        {
            field: 'id',
            headerName: 'No.',
            width: 80,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500, width: '100%', textAlign: 'center' }}>
                    {params.row.id}
                </Typography>
            ),
        },
        {
            field: 'rating',
            headerName: 'Rating',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                    <Rating
                        value={params.row.rating}
                        readOnly
                        size="small"
                        sx={{
                            '& .MuiRating-iconFilled': {
                                color: getStarColor(params.row.rating),
                            },
                        }}
                    />
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5 }}>
                        {params.row.rating}/5
                    </Typography>
                </Box>
            ),
        },
        {
            field: 'review',
            headerName: 'Review',
            minWidth: 250,
            flex: 2,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.review}>
                    <Typography
                        onClick={() => handleOpenDialog(params.row)}
                        sx={{
                            cursor: 'pointer',
                            fontSize: '0.92rem',
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            textAlign: 'center',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        {params.row.review}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'sender',
            headerName: 'Sender',
            minWidth: 140,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.sender.company ? `${params.row.sender.name} (${params.row.sender.company})` : params.row.sender.name}>
                    <Box width="100%" textAlign="center">
                        <Typography sx={{ fontSize: '0.93rem', fontWeight: 500 }}>
                            {params.row.sender.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {params.row.sender.company || params.row.sender.email || 'N/A'}
                        </Typography>
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'recipient',
            headerName: 'Recipient',
            minWidth: 140,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.recipient.company ? `${params.row.recipient.name} (${params.row.recipient.company})` : params.row.recipient.name}>
                    <Box width="100%" textAlign="center">
                        <Typography sx={{ fontSize: '0.93rem', fontWeight: 500 }}>
                            {params.row.recipient.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                            {params.row.recipient.company || params.row.recipient.email || 'N/A'}
                        </Typography>
                    </Box>
                </Tooltip>
            ),
        },
        {
            field: 'date',
            headerName: 'Date',
            minWidth: 180,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary', width: '100%', textAlign: 'center' }}>
                    {params.row.date}
                </Typography>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: 120,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" width="100%">
                    <Tooltip title="Copy review">
                        <IconButton
                            size="small"
                            onClick={() => handleCopyClick(params.row.id, params.row.review)}
                            sx={{ color: copiedStates[params.row.id] ? 'success.main' : 'primary.main', p: 0.5 }}
                        >
                            {copiedStates[params.row.id] ? (
                                <CheckCircleIcon fontSize="small" />
                            ) : (
                                <ContentCopyIcon fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete rating">
                        <IconButton
                            size="small"
                            onClick={() => confirmDelete(params.row)}
                            sx={{ color: 'error.main', p: 0.5 }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ),
        },
    ];


    const handleDownloadAllExcel = async () => {
        if (!filteredRatings || filteredRatings.length === 0) return;

        try {
            // Convert data for export
            const data = filteredRatings.map((rating) => ({
                "No.": rating.id,
                "Rating": `${rating.rating}/5`,
                "Review": rating.review,
                "Sender": rating.sender?.name,
                "Sender Company": rating.sender?.company || "N/A",
                "Recipient": rating.recipient?.name,
                "Recipient Company": rating.recipient?.company || "N/A",
                "Category": rating.category,
                "Date": rating.date,
            }));

            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Ratings");

            // Create columns dynamically
            worksheet.columns = Object.keys(data[0]).map((key) => ({
                header: key,
                key: key,
                width: 25, // Better width for reading
            }));

            // Bold header row
            worksheet.getRow(1).font = { bold: true };

            // Insert data
            worksheet.addRows(data);

            // Generate file
            const buffer = await workbook.xlsx.writeBuffer();

            saveAs(
                new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                }),
                "all_ratings.xlsx"
            );
        } catch (error) {
            console.error("Error generating Excel:", error);
        }
    };

    return (
        <MiniDrawer header="Company User Ratings">
            <Box sx={{ width: '100%', mt: 4, px: isMobile ? 2 : 4 }}>
                <Paper elevation={3} sx={styles.headerSection}>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box display="flex" alignItems="center">
                                <StarIcon sx={{ fontSize: 40, mr: 3 }} />
                                <Box>
                                    <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                                        Ratings
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Average Rating: {averageRating}/5 ({filteredRatings.length} reviews)
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack
                                direction="row"
                                spacing={2}
                                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                                flexWrap="wrap"
                            >
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Filter by Stars</InputLabel>
                                    <Select
                                        value={starFilter}
                                        label="Filter by Stars"
                                        onChange={(e) => setStarFilter(e.target.value)}
                                        sx={styles.filterSelect}
                                    >
                                        <MenuItem value="all">All Ratings</MenuItem>
                                        <MenuItem value="5">5 Stars</MenuItem>
                                        <MenuItem value="4">4 Stars</MenuItem>
                                        <MenuItem value="3">3 Stars</MenuItem>
                                        <MenuItem value="2">2 Stars</MenuItem>
                                        <MenuItem value="1">1 Star</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    sx={styles.refreshButton}
                                >
                                    Refresh
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleDownloadAllExcel}
                                    sx={styles.refreshButton}
                                >
                                    Download Excel
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Ratings Table */}
                <Paper elevation={2} sx={styles.ratingsTable}>
                    {loading ? (
                        <Box sx={styles.loadingBox}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : filteredRatings.length === 0 ? (
                        <Box sx={styles.emptyRatingsBox}>
                            <StarBorderIcon sx={styles.emptyRatingsIcon} />
                            <Typography variant="h5" color="text.secondary" sx={styles.emptyRatingsText}>
                                No ratings found
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                {starFilter !== 'all' ? `No ${starFilter} star ratings` : 'No ratings available yet'}
                            </Typography>
                        </Box>
                    ) : (
                        <DataGrid
                            rows={filteredRatings}
                            columns={columns}
                            autoHeight
                            disableRowSelectionOnClick
                            checkboxSelection
                            rowSelectionModel={selectedIds}
                            onRowSelectionModelChange={(newSelection) => {
                                setSelectedIds(newSelection);
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                    csvOptions: { allColumns: true },
                                },
                            }}
                            sx={styles.dataGrid}
                        />
                    )}
                </Paper>

                {/* Full Review Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleCloseDialog}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 0,
                            maxWidth: { xs: '95vw', sm: '700px', md: '800px' },
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                        }
                    }}
                >
                    {selectedRating && (
                        <>
                            <DialogTitle sx={{
                                px: { xs: 2, sm: 3, md: 4 },
                                py: 2,
                                borderBottom: '1px solid #eee',
                                background: '#fafbfc',
                                flexShrink: 0,
                                minHeight: 'fit-content'
                            }}>
                                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                    <Rating
                                        value={selectedRating.rating}
                                        readOnly
                                        size="large"
                                        sx={{
                                            '& .MuiRating-iconFilled': {
                                                color: getStarColor(selectedRating.rating),
                                            },
                                        }}
                                    />
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color: 'text.primary',
                                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                            wordBreak: 'break-word',
                                            flex: 1,
                                            minWidth: 0
                                        }}
                                    >
                                        {selectedRating.rating}/5 Stars
                                    </Typography>
                                    <Chip
                                        label={selectedRating.category.charAt(0).toUpperCase() + selectedRating.category.slice(1)}
                                        size="small"
                                        color="primary"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.85rem',
                                            px: 1.5,
                                            flexShrink: 0
                                        }}
                                    />
                                </Box>
                            </DialogTitle>
                            <DialogContent
                                sx={{
                                    px: { xs: 2, sm: 3, md: 4 },
                                    py: 2,
                                    overflow: 'auto',
                                    background: '#fff',
                                    flex: 1,
                                    minHeight: 0,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#c1c1c1',
                                        borderRadius: '4px',
                                        '&:hover': {
                                            background: '#a8a8a8',
                                        },
                                    },
                                }}
                            >
                                <Box sx={styles.dialogBox}>
                                    <Paper sx={styles.messagePaper}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                            Review
                                        </Typography>
                                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                            {selectedRating.review}
                                        </Typography>
                                    </Paper>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={styles.senderRecipientPaper}>
                                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                                    Sender Details
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Name:</strong> {selectedRating.sender.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Email:</strong> {selectedRating.sender.email || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Phone:</strong> {selectedRating.sender.phone || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Company:</strong> {selectedRating.sender.company || 'N/A'}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Paper sx={styles.senderRecipientPaper}>
                                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                                    Recipient Details
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Name:</strong> {selectedRating.recipient.name}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Email:</strong> {selectedRating.recipient.email || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                    <strong>Phone:</strong> {selectedRating.recipient.phone || 'N/A'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Company:</strong> {selectedRating.recipient.company || 'N/A'}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>

                                    <Paper sx={styles.senderRecipientPaper}>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                            Additional Information
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Date:</strong> {selectedRating.date}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Status:</strong> {selectedRating.status}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Category:</strong> {selectedRating.category}
                                        </Typography>
                                    </Paper>
                                </Box>
                            </DialogContent>
                            <DialogActions sx={styles.dialogActions}>
                                <Button
                                    onClick={handleCloseDialog}
                                    variant="outlined"
                                    sx={styles.dialogButton}
                                >
                                    Close
                                </Button>
                                <Button
                                    onClick={() => handleCopyClick(selectedRating.id, selectedRating.review)}
                                    variant="contained"
                                    startIcon={<ContentCopyIcon />}
                                    sx={styles.dialogButton}
                                >
                                    Copy Review
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteConfirmDialog}
                    onClose={() => {
                        setDeleteConfirmDialog(false);
                        setRatingToDelete(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: 3,
                            p: 0,
                        }
                    }}
                >
                    <DialogTitle sx={{
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid #eee',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}>
                        <DeleteIcon sx={{ color: 'error.main', fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Delete Rating
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ px: 3, py: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Are you sure you want to delete this rating?
                        </Typography>
                        {ratingToDelete && (
                            <Box sx={{
                                p: 2,
                                bgcolor: '#f5f5f5',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0'
                            }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Rating:</strong> {ratingToDelete.rating}/5 stars
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Review:</strong> {ratingToDelete.review.length > 50
                                        ? `${ratingToDelete.review.substring(0, 50)}...`
                                        : ratingToDelete.review}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>From:</strong> {ratingToDelete.sender.name} <strong>To:</strong> {ratingToDelete.recipient.name}
                                </Typography>
                            </Box>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                        <Button
                            onClick={() => {
                                setDeleteConfirmDialog(false);
                                setRatingToDelete(null);
                            }}
                            variant="outlined"
                            disabled={deleteLoading}
                            sx={{ minWidth: 100 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleDelete(ratingToDelete?.id)}
                            variant="contained"
                            color="error"
                            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
                            disabled={deleteLoading}
                            sx={{ minWidth: 100 }}
                        >
                            {deleteLoading ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </MiniDrawer>
    );
};

export default CompanyUserRating; 